import type { AnalyticsBucket, AnalyticsSummary } from '@shared/analytics';
import type { ProjectSummary } from '@shared/api';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, BarChart3, Building2, CheckCircle2, Copy, Users } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { Card, CardBody, CardHeader, Stat } from '../../components/ui/card';
import { Alert, Badge, EmptyState, Spinner } from '../../components/ui/feedback';
import { Select } from '../../components/ui/form';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/query';
import { errorMessage } from '../../lib/session';

function n(value: number): string {
  return new Intl.NumberFormat().format(value);
}

function percent(part: number, whole: number): string {
  if (whole <= 0) return '0%';
  return `${String(Math.round((part / whole) * 100))}%`;
}

/** Horizontal share bar — the only chart this page needs, and it degrades to a number. */
function ShareBar({ buckets }: { buckets: AnalyticsBucket[] }) {
  const max = buckets.reduce((highest, bucket) => Math.max(highest, bucket.count), 0);
  if (max === 0) return <p className="text-[13px] text-muted-foreground">Nothing recorded yet.</p>;

  return (
    <ul className="space-y-1.5">
      {buckets.map((bucket) => (
        <li key={bucket.label} className="grid grid-cols-[minmax(0,1fr)_60px] items-center gap-2">
          <span className="min-w-0">
            <span className="mb-0.5 block truncate text-[13px] text-foreground">{bucket.label}</span>
            <span className="block h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <span
                className="block h-full rounded-full bg-accent"
                style={{ width: `${String(Math.round((bucket.count / max) * 100))}%` }}
              />
            </span>
          </span>
          <span className="tabular text-right text-[13px] text-muted-foreground">{n(bucket.count)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * What the collected data actually looks like.
 *
 * Every figure is counted in the database from rows this workspace collected. The page states
 * that plainly rather than letting a reader assume the counts describe a whole market.
 */
export function AnalyticsPage() {
  const [params, setParams] = useSearchParams();
  const projectId = params.get('project') ?? undefined;

  const projects = useQuery({ queryKey: queryKeys.projects, queryFn: () => api.get<ProjectSummary[]>('/api/projects') });

  const summary = useQuery({
    queryKey: queryKeys.analytics(projectId),
    queryFn: () =>
      api.get<AnalyticsSummary>(`/api/analytics/summary${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`),
  });

  const data = summary.data;

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-5">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Insights</h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">What your research has collected so far.</p>
        </div>
        <label className="flex items-center gap-1.5">
          <span className="text-[13px] text-muted-foreground">Project</span>
          <Select
            className="h-9 w-56"
            value={projectId ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              setParams(value ? { project: value } : {});
            }}
          >
            <option value="">All projects</option>
            {projects.data?.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        </label>
      </header>

      {summary.isPending ? (
        <div className="flex items-center gap-2 px-1 py-10 text-sm text-muted-foreground">
          <Spinner /> Counting…
        </div>
      ) : summary.isError ? (
        <Alert tone="error">{errorMessage(summary.error)}</Alert>
      ) : !data ? null : data.totals.accounts === 0 ? (
        <EmptyState
          icon={<BarChart3 className="h-6 w-6" />}
          title="Nothing to measure yet"
          description="Run a research project and its numbers will appear here."
        />
      ) : (
        <>
          <Alert className="mb-4" tone="info" title="How to read these numbers">
            {data.framing}
          </Alert>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Stat label="Businesses" value={n(data.totals.accounts)} icon={<Building2 className="h-4 w-4" />} />
            <Stat
              label="Contactable"
              value={n(data.coverage.withPhone + data.coverage.withEmail - data.coverage.withBoth)}
              hint={`${percent(data.coverage.withPhone + data.coverage.withEmail - data.coverage.withBoth, data.coverage.total)} have a phone or an email`}
              icon={<CheckCircle2 className="h-4 w-4" />}
            />
            <Stat label="People" value={n(data.totals.people)} icon={<Users className="h-4 w-4" />} />
            <Stat
              label="Duplicates to review"
              value={n(data.totals.duplicatesPending)}
              hint={data.totals.duplicatesPending > 0 ? 'Pairs the merge step was not confident about' : 'Nothing waiting'}
              icon={<Copy className="h-4 w-4" />}
            />
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader title="Contact coverage" description="How reachable the collected businesses are." />
              <CardBody>
                <ShareBar
                  buckets={[
                    { label: 'Has a phone', value: 'phone', count: data.coverage.withPhone },
                    { label: 'Has an email', value: 'email', count: data.coverage.withEmail },
                    { label: 'Has both', value: 'both', count: data.coverage.withBoth },
                    { label: 'Has a website', value: 'website', count: data.coverage.withWebsite },
                    { label: 'No contact details at all', value: null, count: data.coverage.withNeither },
                  ]}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Top cities" description="Where the collected businesses are." />
              <CardBody>
                <ShareBar buckets={data.topCities} />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Top categories" description="What kind of businesses these are." />
              <CardBody>
                <ShareBar buckets={data.topCategories} />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Ratings" description="Only businesses whose source publishes a rating." />
              <CardBody>
                <ShareBar buckets={data.ratings} />
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="How complete each record is"
                description={
                  data.completeness.average === null
                    ? 'Share of the selected attributes that were actually found.'
                    : `Average ${percent(data.completeness.average, 1)} of selected attributes filled in.`
                }
              />
              <CardBody>
                <ShareBar buckets={data.completeness.buckets} />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Where the data came from" description="A business can be confirmed by several sources." />
              <CardBody>
                {data.sources.length === 0 ? (
                  <p className="text-[13px] text-muted-foreground">No sources recorded yet.</p>
                ) : (
                  <table className="w-full text-[length:var(--text-table)]">
                    <thead>
                      <tr className="border-b border-border text-left text-muted-foreground">
                        <th scope="col" className="py-1.5 font-medium">
                          Source
                        </th>
                        <th scope="col" className="py-1.5 text-right font-medium">
                          Businesses
                        </th>
                        <th scope="col" className="py-1.5 text-right font-medium">
                          Only source
                        </th>
                        <th scope="col" className="py-1.5 text-right font-medium">
                          Raw records
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sources.map((source) => (
                        <tr key={source.source} className="border-b border-border last:border-b-0">
                          <td className="py-1.5 text-foreground">{source.label}</td>
                          <td className="tabular py-1.5 text-right text-foreground">{n(source.accounts)}</td>
                          <td className="tabular py-1.5 text-right text-muted-foreground">{n(source.exclusive)}</td>
                          <td className="tabular py-1.5 text-right text-muted-foreground">{n(source.records)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardBody>
            </Card>
          </div>

          {data.health.length > 0 ? (
            <Card className="mt-4">
              <CardHeader
                title="Source health"
                description="Request outcomes per source. Blocked pages are reported and skipped, never worked around."
              />
              <CardBody className="space-y-2">
                {data.health.map((row) => (
                  <div key={`${row.source}:${row.variant}`} className="border-b border-border pb-2 last:border-b-0 last:pb-0">
                    <div className="flex flex-wrap items-center gap-2 text-[13px]">
                      <span className="font-medium text-foreground">
                        {row.source}
                        {row.variant ? ` · ${row.variant}` : ''}
                      </span>
                      <Badge tone={row.failures === 0 && row.blocked === 0 ? 'success' : row.blocked > 0 ? 'warning' : 'neutral'}>
                        {n(row.successes)} ok
                      </Badge>
                      {row.failures > 0 ? <Badge tone="danger">{n(row.failures)} failed</Badge> : null}
                      {row.blocked > 0 ? <Badge tone="warning">{n(row.blocked)} blocked</Badge> : null}
                      {row.rateLimited > 0 ? <Badge tone="neutral">{n(row.rateLimited)} rate limited</Badge> : null}
                      <span className="tabular text-muted-foreground">{n(row.records)} records</span>
                    </div>
                    {row.alerts.map((alert) => (
                      <p key={alert} className="mt-1 flex items-start gap-1.5 text-[13px] text-warning">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {alert}
                      </p>
                    ))}
                  </div>
                ))}
              </CardBody>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}
